import React, { createContext, useContext, useEffect, useState } from "react";

const STORAGE_KEY = "dr-favourite-runners";

type FavouriteRunnersContextType = {
  favourites: string[];
  isFavourite: (runnerId: string) => boolean;
  toggleFavourite: (runnerId: string) => void;
  removeFavourite: (runnerId: string) => void;
};

const FavouriteRunnersContext = createContext<FavouriteRunnersContextType | null>(null);

export function FavouriteRunnersProvider({ children }: { children: React.ReactNode }) {
    const [favourites, setFavourites] = useState<string[]>([]);
    const [loaded, setLoaded] = useState(false);

    useEffect(() => {
        const saved = localStorage.getItem(STORAGE_KEY);
        if (saved) {
            setFavourites(JSON.parse(saved));
        }
        setLoaded(true);
    }, []);

    useEffect(() => {
        if (!loaded) return;
        localStorage.setItem(STORAGE_KEY, JSON.stringify(favourites));
    }, [favourites, loaded]);

    const isFavourite = (runnerId: string) => favourites.includes(runnerId);

    const toggleFavourite = (runnerId: string) => {
        setFavourites(prev =>
        prev.includes(runnerId)
            ? prev.filter(id => id !== runnerId)
            : [...prev, runnerId]
        );
    };

    const removeFavourite = (runnerId: string) => {
        setFavourites(prev => prev.filter(id => id !== runnerId));
    };

    return (
        <FavouriteRunnersContext.Provider
        value={{ favourites, isFavourite, toggleFavourite, removeFavourite }}
        >
        {children}
        </FavouriteRunnersContext.Provider>
    );
}

export function useFavouriteRunners() {
  const ctx = useContext(FavouriteRunnersContext);
  if (!ctx) throw new Error("useFavouriteRunners must be used inside FavouriteRunnersProvider");
  return ctx;
}
